import type { FormField } from "../../types/form";

export interface ValidationResult {
	success: boolean;
	error?: string;
}

export interface FileValidationResult extends ValidationResult {
	file?: File;
}

/**
 * Validates a file before attempting upload
 * @param file The file to validate
 * @param field The form field configuration for the file field
 * @returns ValidationResult with success/error status
 */
export function validateFile(
	file: File,
	field: FormField,
): FileValidationResult {
	// Ensure this is a file field
	if (field.type !== "file") {
		return {
			success: false,
			error: "Field is not configured for file uploads",
		};
	}

	// Validate file size
	const maxSizeMb = field.maxSizeMb || 10; // Default to 10MB
	const maxSizeBytes = maxSizeMb * 1024 * 1024;

	if (file.size > maxSizeBytes) {
		return {
			success: false,
			error: `File size exceeds maximum allowed size of ${maxSizeMb}MB. Your file is ${formatFileSize(file.size)}.`,
		};
	}

	// Validate content type if accept pattern is specified
	if (
		field.accept &&
		!matchesAcceptPattern(file.type, field.accept, file.name)
	) {
		return {
			success: false,
			error: `File type '${file.type}' is not allowed. Expected: ${field.accept}`,
		};
	}

	return {
		success: true,
		file,
	};
}

/**
 * Validates multiple files for a file field that supports multiple uploads
 * @param files Array of files to validate
 * @param field The form field configuration
 * @returns Array of validation results for each file
 */
export function validateFiles(
	files: File[],
	field: FormField,
): FileValidationResult[] {
	if (field.type !== "file") {
		return files.map(() => ({
			success: false,
			error: "Field is not configured for file uploads",
		}));
	}

	return files.map((file) => validateFile(file, field));
}

/**
 * Checks if a content type matches an accept pattern
 * @param contentType The MIME type of the file
 * @param acceptPattern The accept pattern (e.g., 'image/*', '.pdf', 'application/pdf')
 * @returns true if the content type matches the pattern
 */
export function matchesAcceptPattern(
	contentType: string,
	acceptPattern: string,
	fileName?: string,
): boolean {
	const normalizedPattern = acceptPattern.trim();
	if (!normalizedPattern || normalizedPattern === "*/*") {
		return true;
	}

	return normalizedPattern
		.split(",")
		.map((pattern) => pattern.trim())
		.filter(Boolean)
		.some((pattern) =>
			matchesSinglePattern(contentType, pattern, fileName),
		);
}

/**
 * Helper function to match a single pattern
 */
function matchesSinglePattern(
	contentType: string,
	pattern: string,
	fileName?: string,
): boolean {
	if (!pattern) return false;

	// Handle file extension patterns
	if (pattern.startsWith(".")) {
		const extension = pattern.slice(1).toLowerCase();
		if (fileName) {
			const normalizedName = fileName.trim().toLowerCase();
			const lastDot = normalizedName.lastIndexOf(".");
			if (lastDot === -1) return false;
			return normalizedName.slice(lastDot + 1) === extension;
		}
		return contentType.toLowerCase().endsWith(extension);
	}

	// Handle MIME type patterns like 'image/*'
	if (pattern.endsWith("/*")) {
		const baseType = pattern.slice(0, -2).toLowerCase();
		return contentType.toLowerCase().startsWith(`${baseType}/`);
	}

	// Exact MIME type match
	return contentType.toLowerCase() === pattern.toLowerCase();
}

/**
 * Formats file size in human-readable format
 * @param bytes File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
	if (bytes === 0) return "0 Bytes";

	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	const size = Number.parseFloat((bytes / k ** i).toFixed(1));
	return `${size} ${sizes[i]}`;
}

/**
 * Gets the file extension from a MIME type
 * @param mimeType The MIME type
 * @returns File extension without the dot
 */
export function getFileExtensionFromMimeType(mimeType: string): string {
	const mimeToExt: Record<string, string> = {
		"application/pdf": "pdf",
		"image/jpeg": "jpg",
		"image/png": "png",
		"image/gif": "gif",
		"image/webp": "webp",
		"text/plain": "txt",
		"application/msword": "doc",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
			"docx",
		"application/vnd.ms-excel": "xls",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
		"application/zip": "zip",
		"application/json": "json",
		"text/csv": "csv",
	};

	return mimeToExt[mimeType] || "bin";
}
